/**
 * UpgradeModal
 *
 * Shown when a user hits a feature gate or quota limit.
 * Two modes:
 *   - 'quota': Daily generation limit reached (existing behavior)
 *   - 'feature': Feature requires Pro subscription
 * Displays Free vs Pro comparison and provides a Stripe checkout redirect.
 */

import { useAuth } from '@contexts/AuthContext';
import { useToast } from '@contexts/ToastContext';
import type { AnalysisMode } from '@parlay-picker/shared/analysisMode';
import { ADMIN_BILLING_UNAVAILABLE } from '@parlay-picker/shared/authMessages';
import type { GatedFeature } from '@parlay-picker/shared/featureAccess';
import {
  FEATURE_DESCRIPTIONS,
  FEATURE_DISPLAY_NAMES,
  getRequiredTier,
} from '@parlay-picker/shared/featureAccess';
import {
  SUBSCRIPTION_TIERS,
  type SubscriptionTier,
  TIER_PICKS_QUOTAS,
} from '@parlay-picker/shared/subscriptionTier';
import { Clock, Lock, Zap } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { createPortal } from 'react-dom';
import { API_ENDPOINTS, apiUrl } from '../../config/api';

interface QuotaUpgradeProps {
  mode: 'quota';
  onDismiss: () => void;
  analysisMode?: AnalysisMode;
  used: number;
  limit: number;
  resetsAt: string;
}

interface FeatureUpgradeProps {
  mode: 'feature';
  onDismiss: () => void;
  feature: GatedFeature;
}

// Legacy shape (no mode field) — backward compat with existing callers
interface LegacyUpgradeProps {
  onDismiss: () => void;
  mode?: AnalysisMode;
  used: number;
  limit: number;
  resetsAt: string;
}

type UpgradeModalProps = QuotaUpgradeProps | FeatureUpgradeProps | LegacyUpgradeProps;

/** Type guard: is this a feature-gate modal? */
function isFeatureMode(props: UpgradeModalProps): props is FeatureUpgradeProps {
  return props.mode === 'feature';
}

const TIER_INFO = {
  pro: { name: 'Pro', price: '$24.99/mo' },
  premium: { name: 'Premium', price: '$39.99/mo' },
  elite: { name: 'Elite', price: '$79.99/mo' },
} as const;

/** Check if current tier is at or above the target tier */
function isAtOrAboveTier(current: SubscriptionTier, target: 'pro' | 'premium' | 'elite'): boolean {
  return SUBSCRIPTION_TIERS.indexOf(current) >= SUBSCRIPTION_TIERS.indexOf(target);
}

/** Determine which tier the user should upgrade to based on modal context */
function getTargetTier(
  props: UpgradeModalProps,
  currentTier: SubscriptionTier,
): 'pro' | 'premium' | 'elite' | null {
  if (isFeatureMode(props)) {
    const target = getRequiredTier(props.feature);
    return isAtOrAboveTier(currentTier, target) ? null : target;
  }
  // Quota mode: suggest next tier up from current
  if (currentTier === 'free') return 'pro';
  if (currentTier === 'pro') return 'premium';
  if (currentTier === 'premium') return 'elite';
  // Elite/admin — no upgrade path
  return null;
}

// Portaled so a transformed ancestor (the picks card scales on hover) cannot become the
// containing block for the fixed overlay.
export default function UpgradeModal(props: UpgradeModalProps): ReactElement {
  return createPortal(<UpgradeModalPanel {...props} />, document.body);
}

function UpgradeModalPanel(props: UpgradeModalProps): ReactElement {
  const { authFetch, user } = useAuth();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Admins have no billing; a feature gate previews what a free customer sees.
  const isAdmin = user?.role === 'admin';
  const currentTier: SubscriptionTier =
    isAdmin && isFeatureMode(props) ? 'free' : (user?.role ?? 'free');
  const targetTier = getTargetTier(props, currentTier);

  const handleUpgrade = async (): Promise<void> => {
    if (targetTier === null) return;
    if (isAdmin) {
      showToast(ADMIN_BILLING_UNAVAILABLE, 'info');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const res = await authFetch(apiUrl(API_ENDPOINTS.SUBSCRIPTION_CHECKOUT), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tier: targetTier }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || 'Failed to start checkout');
      }

      const data = await res.json();
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      setError((err as Error).message);
      setIsLoading(false);
    }
  };

  if (targetTier === null) {
    // Quota exhausted with no upgrade path — show "wait for reset" UI
    if (!isFeatureMode(props)) {
      const resetDate = new Date(props.resetsAt);
      const now = new Date();
      const hoursUntilReset = Math.max(
        0,
        Math.ceil((resetDate.getTime() - now.getTime()) / (1000 * 60 * 60)),
      );

      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={props.onDismiss}
          />
          <div className="relative bg-theme-surface border border-theme-border rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center bg-theme-warning/10">
                <Clock className="w-8 h-8 text-theme-warning" />
              </div>
            </div>
            <h3 className="text-xl font-bold text-center mb-2 text-theme-text">
              Daily Limit Reached
            </h3>
            <p className="text-theme-text-secondary text-center mb-1">
              You've used{' '}
              <span className="text-theme-text font-semibold">
                {props.used}/{props.limit}
              </span>{' '}
              generations today.
            </p>
            <p className="text-theme-text-tertiary text-center text-sm mb-5">
              Resets in ~{hoursUntilReset} hour{hoursUntilReset !== 1 ? 's' : ''}
            </p>
            <button
              type="button"
              onClick={props.onDismiss}
              className="w-full py-2.5 rounded-lg bg-theme-surface-hover hover:bg-theme-border text-white font-medium transition"
            >
              Got It
            </button>
          </div>
        </div>
      );
    }

    // A customer whose plan already includes the gated feature: nothing to sell. Never an admin,
    // who is priced as a free customer in feature mode above.
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={props.onDismiss} />
        <div className="relative bg-theme-surface border border-theme-border rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200">
          <h3 className="text-xl font-bold text-center mb-2 text-theme-text">
            {FEATURE_DISPLAY_NAMES[props.feature]} Is Included
          </h3>
          <p className="text-theme-text-secondary text-center mb-5">
            Your current plan already includes this.
          </p>
          <button
            type="button"
            onClick={props.onDismiss}
            className="w-full py-2.5 rounded-lg bg-theme-surface-hover hover:bg-theme-border text-white font-medium transition"
          >
            Got It
          </button>
        </div>
      </div>
    );
  }

  const tierInfo = TIER_INFO[targetTier];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={props.onDismiss} />

      {/* Modal */}
      <div className="relative bg-theme-surface border border-theme-border rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200">
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div
            className={`w-16 h-16 rounded-full flex items-center justify-center ${
              isFeatureMode(props) ? 'bg-theme-primary/10' : 'bg-theme-warning/10'
            }`}
          >
            {isFeatureMode(props) ? (
              <Lock className="w-8 h-8 text-theme-primary" />
            ) : (
              <Zap className="w-8 h-8 text-theme-warning" />
            )}
          </div>
        </div>

        {/* Content — branched by mode */}
        {isFeatureMode(props) ? (
          <FeatureContent feature={props.feature} targetTier={targetTier} />
        ) : (
          <QuotaContent
            analysisMode={
              'analysisMode' in props
                ? props.analysisMode
                : props.mode !== 'quota'
                  ? props.mode
                  : undefined
            }
            used={props.used}
            limit={props.limit}
            resetsAt={props.resetsAt}
            targetTier={targetTier}
          />
        )}

        {/* Error */}
        {error && <p className="text-theme-danger text-sm text-center mb-3">{error}</p>}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={props.onDismiss}
            className="flex-1 py-2.5 rounded-lg bg-theme-surface-hover hover:bg-theme-border text-white font-medium transition"
          >
            Maybe Later
          </button>
          <button
            type="button"
            onClick={handleUpgrade}
            disabled={isLoading}
            className="flex-1 py-2.5 rounded-lg bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill font-medium transition disabled:bg-theme-surface-hover disabled:text-theme-text-tertiary disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Loading...
              </span>
            ) : (
              `Upgrade to ${tierInfo.name}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Feature Gate Content
// =============================================================================

function FeatureContent({
  feature,
  targetTier,
}: {
  feature: GatedFeature;
  targetTier: 'pro' | 'premium' | 'elite';
}): ReactElement {
  const displayName = FEATURE_DISPLAY_NAMES[feature];
  const description = FEATURE_DESCRIPTIONS[feature];
  const tierInfo = TIER_INFO[targetTier];
  const tierQuota = TIER_PICKS_QUOTAS[targetTier];

  return (
    <>
      <h3 className="text-xl font-bold text-center mb-2 text-theme-text">
        {displayName} Requires {tierInfo.name}
      </h3>
      <p className="text-theme-text-secondary text-center mb-5">{description}</p>

      {/* Comparison cards */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="bg-theme-surface-hover border border-theme-border rounded-lg p-3">
          <div className="text-theme-text-tertiary text-xs font-medium mb-2">FREE</div>
          <div className="space-y-1.5 text-sm">
            <div className="text-theme-text-secondary">
              {TIER_PICKS_QUOTAS.free.standard} parlay sets/day
            </div>
            <div className="text-theme-text-tertiary">3 themes</div>
            <div className="text-theme-text-tertiary">5 tracked/day</div>
          </div>
        </div>
        <div className="bg-theme-primary/10 border border-theme-primary/50 rounded-lg p-3">
          <div className="text-theme-primary text-xs font-medium mb-2">
            {tierInfo.name.toUpperCase()} — {tierInfo.price}
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="text-theme-text font-medium">{tierQuota.standard} parlay sets/day</div>
            <div className="text-theme-text-secondary">All themes</div>
            <div className="text-theme-text-secondary">50 tracked parlays/day</div>
            <div className="text-theme-text-secondary">Follow bettors & view shared stats</div>
            {(targetTier === 'premium' || targetTier === 'elite') && (
              <div className="text-theme-text-secondary">{tierQuota.detailed} detailed/day</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// =============================================================================
// Quota Content (existing behavior)
// =============================================================================

function QuotaContent({
  analysisMode,
  used,
  limit,
  resetsAt,
  targetTier,
}: {
  analysisMode?: AnalysisMode;
  used: number;
  limit: number;
  resetsAt: string;
  targetTier: 'pro' | 'premium' | 'elite';
}): ReactElement {
  const resetDate = new Date(resetsAt);
  const now = new Date();
  const hoursUntilReset = Math.max(
    0,
    Math.ceil((resetDate.getTime() - now.getTime()) / (1000 * 60 * 60)),
  );
  const isModeBlocked = limit === 0;
  const tierInfo = TIER_INFO[targetTier];
  const tierQuota = TIER_PICKS_QUOTAS[targetTier];

  return (
    <>
      <h3 className="text-xl font-bold text-center mb-2 text-theme-text">
        {isModeBlocked
          ? `${analysisMode === 'detailed' ? 'Detailed' : (analysisMode ?? 'This')} Mode Requires ${tierInfo.name}`
          : 'Daily Limit Reached'}
      </h3>

      {!isModeBlocked && (
        <>
          <p className="text-theme-text-secondary text-center mb-1">
            You've used{' '}
            <span className="text-theme-text font-semibold">
              {used}/{limit}
            </span>{' '}
            {analysisMode ?? ''} generations today.
          </p>
          <p className="text-theme-text-tertiary text-center text-sm mb-5">
            Resets in ~{hoursUntilReset} hour{hoursUntilReset !== 1 ? 's' : ''}
          </p>
        </>
      )}
      {isModeBlocked && (
        <p className="text-theme-text-secondary text-center mb-5">
          {analysisMode === 'detailed'
            ? 'Detailed analysis is available on the Premium plan.'
            : `${analysisMode ?? 'This'} mode is not available on your current plan.`}
        </p>
      )}

      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="bg-theme-surface-hover border border-theme-border rounded-lg p-3">
          <div className="text-theme-text-tertiary text-xs font-medium mb-2">FREE</div>
          <div className="space-y-1.5 text-sm">
            <div className="text-theme-text-secondary">
              {TIER_PICKS_QUOTAS.free.standard} standard sets/day
            </div>
            <div className="text-theme-text-tertiary">Standard analysis only</div>
          </div>
        </div>
        <div className="bg-theme-primary/10 border border-theme-primary/50 rounded-lg p-3">
          <div className="text-theme-primary text-xs font-medium mb-2">
            {tierInfo.name.toUpperCase()} — {tierInfo.price}
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="text-theme-text font-medium">
              {tierQuota.standard} standard sets/day
            </div>
            {tierQuota.detailed > 0 && (
              <div className="text-theme-text-secondary">{tierQuota.detailed} detailed/day</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
